import { useTranslation } from "react-i18next";
import { formatDate } from "~/helpers/date";
import { RemovableIndicatorChip } from "~/molecules/RemovableIndicatorChip";
import type { DocumentFiltersModel } from "~/hooks/useDocumentFilters";
import {
  FilterOperatorEnum,
  type FilterCondition,
  type FilterField,
  filterFields,
  filterIsDateByField,
} from "~/helpers/documentFilters";

interface ActiveFilterChipsProps {
  filters: DocumentFiltersModel;
}

export const ActiveFilterChips = ({ filters }: ActiveFilterChipsProps) => {
  const { t } = useTranslation();
  const comparatorLabels: Record<FilterCondition["operator"], string> = {
    gte: t(($) => $.library.filters.isAfter),
    lte: t(($) => $.library.filters.isBefore),
    eq: t(($) => $.library.filters.equals),
    in: t(($) => $.library.filters.includes),
    isNull: t(($) => $.library.filters.equals),
  };
  const getComparatorLabel = (operators: FilterCondition["operator"][]) => {
    if (
      operators.length === 2 &&
      operators.includes(FilterOperatorEnum.gte) &&
      operators.includes(FilterOperatorEnum.lte)
    ) {
      return t(($) => $.library.filters.isBetween);
    }

    if (operators.length === 1) {
      const [operator] = operators;
      if (!operator) {
        throw new Error("Missing comparator operator");
      }
      return comparatorLabels[operator];
    }

    throw new Error(
      `Unsupported comparator operator combination: ${operators.join(",")}`,
    );
  };

  const formatFilterValue = (filter: FilterCondition) => {
    const { field } = filter;

    if (filter.operator === FilterOperatorEnum.isNull) {
      return t(($) => $.library.filters.notSet);
    }

    if (filterIsDateByField(field)) {
      if (typeof filter.value !== "string") return "";
      const date = new Date(filter.value);
      return Number.isNaN(date.getTime()) ? "" : formatDate(date);
    }

    const { labelsByValue } = filters.multiSelectFieldData[field];
    if (Array.isArray(filter.value)) {
      return filter.value
        .map((value) => labelsByValue[value] ?? value)
        .join(", ");
    }

    return labelsByValue[filter.value] ?? filter.value;
  };

  const filtersByField = filters.activeFilters.reduce<
    Partial<Record<FilterField, FilterCondition[]>>
  >((acc, filter) => {
    acc[filter.field] = [...(acc[filter.field] ?? []), filter];
    return acc;
  }, {});
  const activeFilterItems = filterFields.flatMap((field) => {
    const fieldFilters = filtersByField[field] ?? [];
    const gteFilter = fieldFilters.find(
      (filter) => filter.operator === FilterOperatorEnum.gte,
    );
    const lteFilter = fieldFilters.find(
      (filter) => filter.operator === FilterOperatorEnum.lte,
    );

    if (gteFilter && lteFilter) {
      return [
        {
          key: `${field}-range`,
          label: t(($) => $.commons.document[field]),
          comparatorLabel: getComparatorLabel([
            FilterOperatorEnum.gte,
            FilterOperatorEnum.lte,
          ]),
          value: `${formatFilterValue(gteFilter)} - ${formatFilterValue(lteFilter)}`,
          onRemove: () => filters.removeFilter(field),
        },
      ];
    }

    return fieldFilters.map((filter) => ({
      key: `${field}-${filter.operator}`,
      label: t(($) => $.commons.document[field]),
      comparatorLabel: getComparatorLabel([filter.operator]),
      value: formatFilterValue(filter),
      onRemove: () => filters.removeFilter(field),
    }));
  });

  if (activeFilterItems.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {activeFilterItems.map((filterItem) => (
        <RemovableIndicatorChip
          key={filterItem.key}
          label={filterItem.label}
          comparatorLabel={filterItem.comparatorLabel}
          value={filterItem.value}
          onRemove={filterItem.onRemove}
        />
      ))}
    </div>
  );
};
